import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Divider,
  Pagination,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import {
  dismissNotification,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../api/notifications";
import {
  disablePushSubscription,
  getPushConfiguration,
  getPushSubscriptions,
  sendTestPushNotification,
} from "../api/pushNotifications";
import { getApiError } from "../api/errors";
import { EmptyPanel, LoadingPanel } from "../components/PageState";
import { useAuth } from "../context/AuthContext";
import PageHeader from "../components/PageHeader";
import MainLayout from "../layouts/MainLayout";
import {
  disablePushOnThisDevice,
  enablePushOnThisDevice,
  getPushCapability,
} from "../services/pushNotifications";
import { formatDate, titleCase } from "../utils/format";

const PAGE_SIZE = 30;

export default function Notifications() {
  const { selectedWorkspace } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [pushConfig, setPushConfig] = useState(null);
  const [pushDevices, setPushDevices] = useState([]);
  const [pushLoading, setPushLoading] = useState(true);
  const [pushSaving, setPushSaving] = useState(false);
  const [pushError, setPushError] = useState("");
  const [pushSuccess, setPushSuccess] = useState("");
  const [capability, setCapability] = useState(() => getPushCapability());

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await getNotifications({
        unread_only: unreadOnly || undefined,
        page,
        page_size: PAGE_SIZE,
      });
      setItems(response.data?.items || []);
      setTotal(Number(response.data?.total || 0));
    } catch (requestError) {
      setError(getApiError(requestError, "Could not load notifications."));
    } finally {
      setLoading(false);
    }
  }, [page, unreadOnly]);

  const loadPush = useCallback(async () => {
    setPushLoading(true);
    setPushError("");
    try {
      const [configResponse, subscriptionsResponse] = await Promise.all([
        getPushConfiguration(),
        getPushSubscriptions(),
      ]);
      setPushConfig(configResponse.data);
      setPushDevices(subscriptionsResponse.data?.items || []);
    } catch (requestError) {
      setPushError(
        getApiError(requestError, "Could not load phone notification settings."),
      );
    } finally {
      setPushLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadPush();
  }, [load, loadPush, selectedWorkspace?.public_id]);

  useEffect(() => {
    setPage(1);
  }, [unreadOnly]);

  const openNotification = async (notification) => {
    try {
      if (notification.status === "unread") {
        await markNotificationRead(notification.public_id);
      }
    } catch {
      // Navigation remains useful even if read-state persistence temporarily fails.
    }
    if (
      notification.resource_type === "order" &&
      notification.resource_public_id
    ) {
      navigate(`/orders?open=${encodeURIComponent(notification.resource_public_id)}`);
      return;
    }
    if (
      notification.resource_type === "conversation" &&
      notification.resource_public_id
    ) {
      navigate(
        `/conversations?open=${encodeURIComponent(notification.resource_public_id)}`,
      );
      return;
    }
    await load();
  };

  const dismiss = async (notificationPublicId) => {
    setSaving(true);
    setError("");
    try {
      await dismissNotification(notificationPublicId);
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not dismiss the notification."));
    } finally {
      setSaving(false);
    }
  };

  const markAll = async () => {
    setSaving(true);
    setError("");
    try {
      await markAllNotificationsRead();
      await load();
    } catch (requestError) {
      setError(getApiError(requestError, "Could not mark notifications as read."));
    } finally {
      setSaving(false);
    }
  };

  const enablePhonePush = async () => {
    setPushSaving(true);
    setPushError("");
    setPushSuccess("");
    try {
      await enablePushOnThisDevice({
        publicKey: pushConfig?.public_key,
        serviceWorkerPath: pushConfig?.service_worker_path,
      });
      setPushSuccess("Phone notifications are enabled on this device.");
      setCapability(getPushCapability());
      await loadPush();
    } catch (requestError) {
      setPushError(
        requestError?.message || "Could not enable phone notifications.",
      );
    } finally {
      setPushSaving(false);
    }
  };

  const disableCurrentDevice = async () => {
    setPushSaving(true);
    setPushError("");
    setPushSuccess("");
    try {
      const disabled = await disablePushOnThisDevice();
      setPushSuccess(
        disabled
          ? "Phone notifications were disabled on this device."
          : "This browser did not have an active push subscription.",
      );
      setCapability(getPushCapability());
      await loadPush();
    } catch (requestError) {
      setPushError(
        requestError?.message || "Could not disable phone notifications.",
      );
    } finally {
      setPushSaving(false);
    }
  };

  const disableDevice = async (publicId) => {
    setPushSaving(true);
    setPushError("");
    setPushSuccess("");
    try {
      await disablePushSubscription(publicId);
      setPushSuccess("The selected device was disabled.");
      await loadPush();
    } catch (requestError) {
      setPushError(
        getApiError(requestError, "Could not disable the selected device."),
      );
    } finally {
      setPushSaving(false);
    }
  };

  const sendTest = async () => {
    setPushSaving(true);
    setPushError("");
    setPushSuccess("");
    try {
      const response = await sendTestPushNotification();
      const count = Number(response.data?.queued_deliveries || 0);
      setPushSuccess(
        count > 0
          ? `Test notification queued for ${count} device${count === 1 ? "" : "s"}.`
          : "No active phone device is subscribed for this workspace.",
      );
    } catch (requestError) {
      setPushError(getApiError(requestError, "Could not send the test notification."));
    } finally {
      setPushSaving(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const activePushDevices = pushDevices.filter((item) => item.status === "active");

  return (
    <MainLayout>
      <PageHeader
        title="Notifications"
        description="Employee handoff and confirmed-order alerts, dashboard history, and phone push delivery."
        actions={(
          <>
            <Stack direction="row" alignItems="center" sx={{ minHeight: 40 }}>
              <Switch
                checked={unreadOnly}
                onChange={(event) => setUnreadOnly(event.target.checked)}
              />
              <Typography variant="body2" fontWeight={800}>Unread only</Typography>
            </Stack>
            <Button variant="outlined" onClick={load} disabled={loading}>
              Refresh
            </Button>
            <Button variant="contained" onClick={markAll} disabled={saving || total === 0}>
              Mark all read
            </Button>
          </>
        )}
      />

      <Card sx={{ mb: 3, borderRadius: 3, boxShadow: "none", border: "1px solid #e2e8f0" }}>
        <CardContent sx={{ p: { xs: 2.2, md: 3 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            gap={2}
          >
            <Box>
              <Typography variant="h6" fontWeight={900}>
                Phone notifications
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.5 }}>
                Receive a phone alert for employee handoffs and confirmed customer orders.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <Button
                variant="contained"
                onClick={enablePhonePush}
                disabled={pushSaving || pushLoading || !pushConfig?.enabled}
              >
                Enable on this device
              </Button>
              <Button
                variant="outlined"
                onClick={sendTest}
                disabled={pushSaving || activePushDevices.length === 0}
              >
                Send test
              </Button>
              <Button
                color="inherit"
                onClick={disableCurrentDevice}
                disabled={pushSaving}
              >
                Disable this device
              </Button>
            </Stack>
          </Stack>

          <Divider sx={{ my: 2.3 }} />

          {!pushLoading && !pushConfig?.enabled && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Web Push is not enabled on the backend yet. Add the VAPID and encryption settings, then restart FastAPI.
            </Alert>
          )}
          {!capability.secureContext && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Phone notifications require HTTPS in production. Localhost is allowed for development.
            </Alert>
          )}
          {capability.requiresIOSInstall && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              On iPhone: open the dashboard in Safari, choose Add to Home Screen, then open the installed app and enable notifications.
            </Alert>
          )}
          {pushError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setPushError("")}>
              {pushError}
            </Alert>
          )}
          {pushSuccess && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setPushSuccess("")}>
              {pushSuccess}
            </Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <Chip label={`Browser permission: ${titleCase(capability.permission)}`} />
            <Chip
              label={`${activePushDevices.length} active device${activePushDevices.length === 1 ? "" : "s"}`}
              color={activePushDevices.length > 0 ? "success" : "default"}
              variant={activePushDevices.length > 0 ? "filled" : "outlined"}
            />
            <Chip
              label={pushConfig?.preview_enabled ? "Lock-screen preview on" : "Private preview"}
              variant="outlined"
            />
          </Stack>

          {pushLoading ? (
            <Typography color="text.secondary">Loading devices…</Typography>
          ) : pushDevices.length === 0 ? (
            <Typography color="text.secondary">
              No phone or browser device has been registered for this workspace.
            </Typography>
          ) : (
            <Stack spacing={1}>
              {pushDevices.map((device) => (
                <Stack
                  key={device.public_id}
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "stretch", sm: "center" }}
                  gap={1.5}
                  sx={{ p: 1.5, border: "1px solid #e2e8f0", borderRadius: 2 }}
                >
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                      <Typography fontWeight={850}>{device.device_name || "Browser device"}</Typography>
                      <Chip
                        size="small"
                        label={titleCase(device.status)}
                        color={device.status === "active" ? "success" : "default"}
                        variant={device.status === "active" ? "filled" : "outlined"}
                      />
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      {device.platform || "Unknown platform"} · Added {formatDate(device.created_at)}
                      {device.last_success_at ? ` · Last delivered ${formatDate(device.last_success_at)}` : ""}
                    </Typography>
                  </Box>
                  {device.status === "active" && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      onClick={() => disableDevice(device.public_id)}
                      disabled={pushSaving}
                    >
                      Disable
                    </Button>
                  )}
                </Stack>
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
        Alerts are saved in PostgreSQL before phone delivery, so an offline device does not remove the dashboard notification.
      </Alert>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {loading ? (
        <LoadingPanel label="Loading notifications…" />
      ) : items.length === 0 ? (
        <EmptyPanel
          icon="🔔"
          title="No notifications"
          description="Customer handoff requests will appear here automatically."
        />
      ) : (
        <Stack spacing={1.4}>
          {items.map((item) => (
            <Card
              key={item.public_id}
              sx={{
                borderRadius: 3,
                border: "1px solid",
                borderColor: item.status === "unread" ? "warning.main" : "#e2e8f0",
                bgcolor: item.status === "unread" ? "#fffbeb" : "white",
                boxShadow: "none",
                position: "relative",
              }}
            >
              <CardActionArea onClick={() => openNotification(item)}>
                <CardContent sx={{ p: 2.2, pb: 5 }}>
                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    gap={1.5}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography fontWeight={900}>{item.title}</Typography>
                        <Chip
                          size="small"
                          label={titleCase(item.status)}
                          color={item.status === "unread" ? "warning" : "default"}
                          variant={item.status === "unread" ? "filled" : "outlined"}
                        />
                      </Stack>
                      {item.body && (
                        <Typography color="text.secondary" sx={{ mt: 0.8 }}>
                          {item.body}
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                      {formatDate(item.created_at)}
                    </Typography>
                  </Stack>
                </CardContent>
              </CardActionArea>
              {item.status !== "resolved" && item.status !== "dismissed" && (
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => dismiss(item.public_id)}
                  disabled={saving}
                  sx={{ position: "absolute", right: 12, bottom: 8 }}
                >
                  Dismiss
                </Button>
              )}
            </Card>
          ))}
        </Stack>
      )}

      {pageCount > 1 && (
        <Stack alignItems="center" sx={{ mt: 3 }}>
          <Pagination
            page={page}
            count={pageCount}
            onChange={(_, value) => setPage(value)}
            color="primary"
            shape="rounded"
          />
        </Stack>
      )}
    </MainLayout>
  );
}
